import { CheckOutlined, EyeOutlined } from "@ant-design/icons"
import { useQuery } from "@tanstack/react-query"
import { Button, Skeleton, Table, Tooltip } from "antd"
import React, { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ContainerFull } from "../components"
import { getAllPlates } from "../endpoints"
import { WellInterface } from "../types"

// export default function AllPlates() {
export const AllPlates = (): JSX.Element => {
    const navigate = useNavigate()
    
    const { isPending, isError, data, error } = useQuery({
        queryKey: ["allplates"],
        queryFn: () => getAllPlates(),
        refetchOnMount: "always",
    })

    // State for selected plates
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

    if (isPending) return <Skeleton />
    if (isError || data.code !== 1000) return <div>Error: {error?.message || data?.message || "Unknown error"}</div>

    // Handler for "Make Run" button
    const handleMakeRun = () => {
        // You can replace this with your actual logic
        if (selectedRowKeys.length === 0) {
            alert("Please select at least one plate to make a run.")
            return
        }
        // Example: navigate to a run creation page with selected plate IDs
        navigate(`/admin/makerun?plates=${selectedRowKeys.join(",")}`)
    }

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <h2>All Plates</h2>
            <div style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    disabled={selectedRowKeys.length === 0}
                    onClick={handleMakeRun}
                    style={{ marginRight: 8 }}
                >
                    Make Run
                </Button>
                <span style={{ color: "#888" }}>
                    {selectedRowKeys.length > 0 ? `${selectedRowKeys.length} plate(s) selected` : ""}
                </span>
            </div>
            <Table
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                }}
                dataSource={data.plates}
                rowKey="id"
                pagination={{ pageSize: 20 }}
                columns={[
                    {
                        title: "Details",
                        dataIndex: "id",
                        align: "center",
                        render: (id: number) => {
                            return (
                                <Tooltip title="view plate">
                                    <EyeOutlined
                                        style={{ color: "blue" }}
                                        onClick={async () => {
                                            navigate(`/admin/plate/${id}`)
                                        }}
                                    />
                                </Tooltip>
                            )
                        },
                    },
                    {
                        title: "Plate ID",
                        dataIndex: "id",
                        render: (id: number) => <Link to={`/admin/plate/${id}`}>{id}</Link>,
                    },
                    {
                        title: "Status",
                        dataIndex: "status",
                        align: "center",
                        render: (status: string) => (
                            <span>{status==='normal' ? '':status}</span>
                        ),
                    },
                    {
                        title: "Orders",
                        dataIndex: "orders",
                        align: "center",
                        render: (_: any, record: any) => {
                            // Collect unique order IDs from all wells' samples
                            const orderIdSet = new Set<number>()
                            record.wells?.forEach((well: WellInterface) => {
                                if (well.sample?.orderId) orderIdSet.add(well.sample.orderId)
                            })
                            const orderIds = Array.from(orderIdSet)
                            return orderIds.length > 0 ? (
                                orderIds.map((id, i) => (
                                    <span key={id}>
                                        <Link to={`/admin/order/${id}`}>{id}</Link>
                                        {i < orderIds.length - 1 && ", "}
                                    </span>
                                ))
                            ) : (
                                <span style={{ color: "#bbb" }}>None</span>
                            )
                        },
                    },
                    {
                        title: "Samples",
                        dataIndex: "id",
                        align: "center",
                        render: (_: any, record: any) => (
                            <span>{record.wells?.filter((x: WellInterface) => x.sample).length ?? 0}</span>
                        ),
                    },

                    {
                        title: "Creator",
                        dataIndex: "Creator",
                        render: (text, record, index) => {
                            return <span>{record.creator?.firstName + " " + record.creator?.lastName}</span>
                        },
                    },
                    {
                        title: "Run ID",
                        dataIndex: "runId",
                        render: (runId: number | null) => {
                            if (!runId) return <span style={{ color: "#bbb" }}>-</span>
                            return <Link to={`/admin/run/${runId}`}>{runId}</Link>
                        },
                    },
                    {
                        title: "Notes",
                        dataIndex: "notes",
                        render: (notes: string) =>
                            notes ? (
                                <span style={{ maxWidth: 200, wordBreak: "break-word", whiteSpace: "normal", display: "inline-block" }}>
                                    {notes}
                                </span>
                            ) : (
                                <span style={{ color: "#bbb" }}>-</span>
                            ),
                    },
                    {
                        title: "Changed",
                        dataIndex: "modifications",
                        align: "center",
                        render: (modifications: string) => (
                            <span>
                                {modifications && modifications.trim().length > 0 ? (
                                    <CheckOutlined style={{ color: "brown", fontWeight: "bold" }} />
                                ) : null}
                            </span>
                        ),
                    },
                    {
                        title: "Created",
                        dataIndex: "ts",
                        render: (ts: string) => new Date(ts).toLocaleString(),
                    },
                ]}
            />
        </ContainerFull>
    )
}
